import React from 'react';
import { Box, Stack, Typography, TypographyProps, useTheme } from '@mui/material';
import { useChartAccessorsContext } from '../../core/ChartRoot';
import { cn } from '../../utils/cn';
import { formatPercentage } from '../../utils/formatters';
import type { ArcAngles } from './PieSlice.utils';
import { computePolylineLabelPosition, computePolylinePoints, LINE_HORIZONTAL_LENGTH, POLYLINE_LABEL_WIDTH } from './polyline.utils';

export const polylineLabelClassNames = {
  line: 'Chart-PiePolyline',
  label: 'Chart-PiePolylineLabel',
  labelTop: 'Chart-PiePolylineLabel--Top',
  labelBottom: 'Chart-PiePolylineLabel--Bottom',
  labelRight: 'Chart-PiePolylineLabel--Right',
  labelLeft: 'Chart-PiePolylineLabel--Left',
} as const;

type PolylineLabelProps<Datum> = {
  datum: Datum;
  angles: ArcAngles;
  ringInnerRadius: number;
  ringOuterRadius: number;
  highlighted: boolean;
  selected: boolean;
  color: string;
  total?: number;
  scale?: number;
};

const ROW_HEIGHT = 18;
const GAP = 2;

const PolylineTopLabel = ({ sx, ...props }: TypographyProps<'span'>) => (
  <Typography
    variant="caption"
    component="span"
    fontSize={12}
    noWrap
    display="block"
    sx={{
      color: t => t.palette.colors.neutrals[600],
      width: 'var(--foreign-object-width)',
      maxWidth: 'var(--foreign-object-width)',
      [`.${polylineLabelClassNames.labelRight} &`]: { textAlign: 'end' },
      [`.${polylineLabelClassNames.labelLeft} &`]: { textAlign: 'start' },
      ...sx,
    }}
    {...props}
  />
);

export const PolylineLabel = <Datum,>({
  datum,
  angles,
  ringInnerRadius,
  ringOuterRadius,
  highlighted,
  selected,
  color,
  total,
  scale = 1,
}: PolylineLabelProps<Datum>) => {
  const theme = useTheme();
  const { labelFormatter, valueFormatter, getValue } = useChartAccessorsContext<Datum>();

  const dimLabel = labelFormatter?.(datum, { where: 'polyline-label' });
  if (!dimLabel) return null;

  const labelRadius = highlighted ? ringOuterRadius : ringInnerRadius;
  const { labelX, labelY, offsetX, showLabel } = computePolylineLabelPosition(angles, labelRadius, selected, highlighted, scale);

  if (!showLabel) return null;

  const { startX, startY, elbowX, elbowY } = computePolylinePoints(angles, ringInnerRadius, labelRadius, scale);

  const value = getValue(datum);
  const formattedValue = valueFormatter?.(datum, { where: 'polyline-label' });
  const showValue = formattedValue !== undefined && formattedValue !== '';
  const share = total ? formatPercentage.format(value / total) : undefined;
  const showValueRow = showValue || share;
  const isRight = offsetX >= 0;
  const { neutrals } = theme.palette.colors;
  const scaledLabelWidth = POLYLINE_LABEL_WIDTH * scale;
  const lineEndX = isRight ? labelX + scaledLabelWidth : labelX - scaledLabelWidth;
  const foreignObjectWidth = (LINE_HORIZONTAL_LENGTH + POLYLINE_LABEL_WIDTH) * scale;
  const foreignObjectX = isRight ? lineEndX - foreignObjectWidth : lineEndX;

  return (
    <>
      <path
        aria-hidden="true"
        className={polylineLabelClassNames.line}
        d={`M ${startX} ${startY} L ${elbowX} ${elbowY} L ${lineEndX} ${labelY}`}
        stroke={color}
        strokeWidth={1.5}
        fill="none"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <foreignObject
        aria-hidden="true"
        className={cn(
          polylineLabelClassNames.label,
          polylineLabelClassNames.labelTop,
          isRight ? polylineLabelClassNames.labelRight : polylineLabelClassNames.labelLeft
        )}
        x={foreignObjectX}
        y={labelY - ROW_HEIGHT - GAP}
        width={foreignObjectWidth}
        height={ROW_HEIGHT}
        style={{
          overflow: 'visible',
          pointerEvents: 'none',
          // @ts-expect-error: css var
          '--foreign-object-width': `${foreignObjectWidth}px`,
        }}>
        {React.isValidElement(dimLabel) ? dimLabel : <PolylineTopLabel>{dimLabel}</PolylineTopLabel>}
      </foreignObject>
      {showValueRow && (
        <foreignObject
          aria-hidden="true"
          className={cn(
            polylineLabelClassNames.label,
            polylineLabelClassNames.labelBottom,
            isRight ? polylineLabelClassNames.labelRight : polylineLabelClassNames.labelLeft
          )}
          x={foreignObjectX}
          y={labelY + GAP}
          width={foreignObjectWidth}
          height={ROW_HEIGHT}
          style={{ overflow: 'visible', pointerEvents: 'none' }}>
          <Stack
            direction={isRight ? 'row-reverse' : 'row'}
            alignItems="center"
            gap={0.75}
            fontSize={12}
            color={t => t.palette.colors.neutrals[800]}>
            {showValue && (
              <Box component="span" sx={{ fontWeight: 600 }}>
                {String(formattedValue)}
              </Box>
            )}
            {showValue && share && (
              <Box
                component="span"
                sx={{
                  width: 1.5,
                  height: 13,
                  backgroundColor: neutrals[350],
                  flexShrink: 0,
                }}
              />
            )}
            {share && <span>{share}</span>}
          </Stack>
        </foreignObject>
      )}
    </>
  );
};
